import {
  Component,
  computed,
  inject,
  input,
  Input,
  output,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  HsButtonModule,
  HsCardModule,
  HsDateTimePickerModule,
  HsFormFieldModule,
  HsIconModule,
  HsInputModule,
} from '@hsetu/ngui-components';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { HsLayerModule } from '@hsetu/ngui-resources';
import { ICON_CLOCK, ICON_PLUS } from '@shared/ui/icons/icon-constants';
import { FormsModule } from '@angular/forms';
import { AppointmentService } from '@modules/sales/services/appointment.service';
import { KfmCardComponent } from '@shared/ui/components/features/card/common/kfm-card.component';
import { GuidGeneratorService } from '@shared/tools/guid-generator.service';
import { Task } from '@modules/sales/models/task.model';
import {
  APPPOINTMENT_DATA_CARD_TITLE_LABEL,
  DATE_FROM_LABEL,
  DATE_TO_LABEL,
  TIME_FROM_LABEL,
  TIME_TO_LABEL,
} from '@modules/sales/i18n/label-text-constants';

const HALF_HOUR_MS = 30 * 60 * 1000;
const DATE_DEBOUNCE_MS = 600;
const PERSIST_DEBOUNCE_MS = 800;

@Component({
  selector: 'kfm-appointment-date-card',
  imports: [
    CommonModule,
    FormsModule,
    HsCardModule,
    HsFormFieldModule,
    HsLayerModule,
    HsInputModule,
    HsIconModule,
    HsDateTimePickerModule,
    KfmCardComponent,
    HsButtonModule,
  ],
  templateUrl: './kfm-appointment-date-card.component.html',
})
export class KfmAppointmentDateCardComponent {
  private readonly _sanitizer = inject(DomSanitizer);
  private readonly _appointmentService = inject(AppointmentService);
  private readonly _guidGenerator = inject(GuidGeneratorService);

  private _dateDebounceTimer: ReturnType<typeof setTimeout> | undefined;
  private _startTimeDebounceTimer: ReturnType<typeof setTimeout> | undefined;
  private _endTimeDebounceTimer: ReturnType<typeof setTimeout> | undefined;
  private _persistDebounceTimer: ReturnType<typeof setTimeout> | undefined;

  protected internalAppointment = signal<Task | undefined>(undefined);

  protected readonly cardTitle = APPPOINTMENT_DATA_CARD_TITLE_LABEL;
  protected readonly dateFromLabel = DATE_FROM_LABEL;
  protected readonly timeFromLabel = TIME_FROM_LABEL;
  protected readonly dateToLabel = DATE_TO_LABEL;
  protected readonly timeToLabel = TIME_TO_LABEL;

  protected get cardIcon(): SafeHtml {
    return this._sanitizer.bypassSecurityTrustHtml(ICON_CLOCK);
  }

  protected readonly iconNew =
    this._sanitizer.bypassSecurityTrustHtml(ICON_PLUS);

  protected readonly startDate = computed(() => {
    const task = this.internalAppointment();
    if (!task?.appointment?.startDate) return new Date();
    return new Date(task.appointment.startDate);
  });

  protected readonly startTime = computed(() => this.startDate());

  protected readonly endDate = computed(() => {
    const task = this.internalAppointment();
    if (!task?.appointment?.endDate) {
      if (task?.appointment?.startDate) {
        const end = new Date(task.appointment.startDate);
        end.setTime(end.getTime() + HALF_HOUR_MS);
        return end;
      }
      return new Date();
    }
    return new Date(task.appointment.endDate);
  });

  protected readonly endTime = computed(() => this.endDate());

  protected readonly hasAppointment = computed(() => {
    const task = this.internalAppointment();
    return (
      task &&
      task.appointment &&
      task.appointment.uId !== this._guidGenerator.emptyUUID
    );
  });

  @Input()
  public set appointment(value: Task) {
    this.internalAppointment.set(value);
  }

  public isEditable = input<boolean>(true);

  public appointmentChanged = output<void>();
  public taskChanged = output<Task>();

  // User changed the start date — keep start time, keep end time unchanged;
  // only if gap < 30 min, move end date forward to match start date (keeping end time),
  // so the end time is preserved and only the date portion is adjusted.
  protected onStartDateChanged(date: Date | undefined) {
    if (!date) return;

    clearTimeout(this._dateDebounceTimer);
    this._dateDebounceTimer = setTimeout(() => {
      const start = new Date(this.startDate());
      start.setFullYear(date.getFullYear(), date.getMonth(), date.getDate());

      const end = new Date(this.endDate());

      if (end.getTime() - start.getTime() < HALF_HOUR_MS) {
        end.setFullYear(date.getFullYear(), date.getMonth(), date.getDate());
        if (end.getTime() - start.getTime() < HALF_HOUR_MS) {
          end.setTime(start.getTime() + HALF_HOUR_MS);
        }
      }

      this.persist(start, end);
    }, DATE_DEBOUNCE_MS);
  }

  // User changed the start time — keep start date, keep end datetime;
  // if gap < 30 min, push end forward to start + 30 min (may cross midnight).
  protected onStartTimeChanged(time: Date | undefined) {
    if (!time || isNaN(time.getTime())) return;

    // Snapshot current signal values immediately (before async timeout)
    const startSnapshot = new Date(this.startDate());
    const endSnapshot = new Date(this.endDate());

    clearTimeout(this._startTimeDebounceTimer);
    this._startTimeDebounceTimer = setTimeout(() => {
      const start = new Date(startSnapshot);
      start.setHours(time.getHours(), time.getMinutes(), 0, 0);

      const end = new Date(endSnapshot);

      if (end.getTime() - start.getTime() < HALF_HOUR_MS) {
        end.setTime(start.getTime() + HALF_HOUR_MS);
      }

      this.persist(start, end);
    }, DATE_DEBOUNCE_MS);
  }

  // User changed the end date — keep end time, keep start unchanged;
  // if gap < 30 min, push end forward to start + 30 min.
  protected onEndDateChanged(date: Date | undefined) {
    if (!date) return;

    clearTimeout(this._dateDebounceTimer);
    this._dateDebounceTimer = setTimeout(() => {
      const start = new Date(this.startDate());

      const end = new Date(this.endDate());
      end.setFullYear(date.getFullYear(), date.getMonth(), date.getDate());

      if (end.getTime() - start.getTime() < HALF_HOUR_MS) {
        end.setTime(start.getTime() + HALF_HOUR_MS);
      }

      this.persist(start, end);
    }, DATE_DEBOUNCE_MS);
  }

  // User changed the end time — keep end date, keep start unchanged;
  // if gap < 30 min, push end forward to start + 30 min.
  protected onEndTimeChanged(time: Date | undefined) {
    if (!time || isNaN(time.getTime())) return;

    // Snapshot current signal values immediately (before async timeout)
    const startSnapshot = new Date(this.startDate());
    const endSnapshot = new Date(this.endDate());

    clearTimeout(this._endTimeDebounceTimer);
    this._endTimeDebounceTimer = setTimeout(() => {
      const start = new Date(startSnapshot);

      const end = new Date(endSnapshot);
      end.setHours(time.getHours(), time.getMinutes(), 0, 0);

      if (end.getTime() - start.getTime() < HALF_HOUR_MS) {
        end.setTime(start.getTime() + HALF_HOUR_MS);
      }

      this.persist(start, end);
    }, DATE_DEBOUNCE_MS);
  }

  protected async onNewAppointmentClicked() {
    const task = this.internalAppointment();

    const appointment = await this._appointmentService.create(
      task.uId,
      task.customer?.uId,
      task.location?.uId,
    );

    if (appointment) {
      task.appointment = appointment;
      this.internalAppointment.set({ ...task });
      this.appointmentChanged.emit();
    }
  }

  // Writes new dates into the signal with a fresh object reference so Angular
  // detects the change, then debounces the backend update so rapid field changes
  // (day, month, year, hour, minute) only trigger one request.
  private persist(startDate: Date, endDate: Date) {
    const salesItem = this.internalAppointment();
    if (!salesItem?.appointment) return;

    this.internalAppointment.set({
      ...salesItem,
      appointment: {
        ...salesItem.appointment,
        startDate,
        endDate,
      },
    });

    this.taskChanged.emit(this.internalAppointment());

    clearTimeout(this._persistDebounceTimer);
    this._persistDebounceTimer = setTimeout(() => {
      this.updateAppointment();
    }, PERSIST_DEBOUNCE_MS);
  }

  private async updateAppointment() {
    const task = this.internalAppointment();
    if (!task?.appointment) return;

    const taskId = task.uId === task.appointment.uId ? undefined : task.uId;

    await this._appointmentService.update(
      task.appointment,
      taskId,
      task.customer?.uId,
      task.location?.uId,
    );
  }
}
